import axios from 'axios';
import { auth } from '../config/firebase';

export interface IShopifyStore {
  id?: string;
  storeName: string;
  storeUrl: string;
  accessToken: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

class StoreManagementService {
  private baseUrl = '/api/stores';

  private async getAuthHeaders() {
    try {
      // Get current user and check authentication
      const user = auth.currentUser;
      if (!user) {
        // Attempt to reload auth state
        await auth.authStateReady();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          // Redirect to login if not authenticated
          window.location.href = '/';
          throw new Error('Not authenticated');
        }
      }

      // Get fresh token
      const token = await user?.getIdToken(true);
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error: any) {
      if (error.code === 'auth/invalid-user-token') {
        // Force sign out if token is invalid
        await auth.signOut();
        window.location.href = '/';
      }
      throw new Error('Authentication failed. Please log in again.');
    }
  }

  async addStore(store: Pick<IShopifyStore, 'storeName' | 'storeUrl' | 'accessToken'>): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(this.baseUrl, store, { headers });
      return response.data.id;
    } catch (error: any) {
      if (error.response?.status === 401) {
        await auth.signOut();
        window.location.href = '/';
        throw new Error('Authentication expired. Please log in again.');
      }
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to add store'
      );
    }
  }

  async getStores(): Promise<IShopifyStore[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.get(this.baseUrl, { headers });
      return response.data.stores.map((store: any) => ({
        ...store,
        createdAt: new Date(store.createdAt),
        updatedAt: new Date(store.updatedAt)
      }));
    } catch (error: any) {
      if (error.response?.status === 401) {
        await auth.signOut();
        window.location.href = '/';
        throw new Error('Authentication expired. Please log in again.');
      }
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to get stores'
      );
    }
  }

  async updateStore(storeId: string, store: Pick<IShopifyStore, 'storeName' | 'storeUrl' | 'accessToken'>): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await axios.put(this.baseUrl, { storeId, ...store }, { headers });
    } catch (error: any) {
      if (error.response?.status === 401) {
        await auth.signOut();
        window.location.href = '/';
        throw new Error('Authentication expired. Please log in again.');
      }
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to update store'
      );
    }
  }

  async deleteStore(storeId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await axios.delete(`${this.baseUrl}?id=${storeId}`, { headers });
    } catch (error: any) {
      if (error.response?.status === 401) {
        await auth.signOut();
        window.location.href = '/';
        throw new Error('Authentication expired. Please log in again.');
      }
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to delete store'
      );
    }
  }

  // Validate store credentials without adding the store
  async validateStoreCredentials(storeUrl: string, accessToken: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.post(
        this.baseUrl, 
        { 
          storeName: 'validation-test',
          storeUrl,
          accessToken,
          validate: true
        },
        { headers }
      );
      return response.status === 200;
    } catch (error: any) {
      return false;
    }
  }
}

export const storeManagementService = new StoreManagementService();
